import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const PROFILE_NAMES: Record<number, string> = { 1: "Defensive", 2: "Cautious", 3: "Balanced", 4: "Growth", 5: "Aggressive" };

type Source = "tier1" | "regulatory" | "operational";

interface Provenance {
  source: Source;
  ref: string;          // e.g. "Tier 1 §6.2" or "COBS 9A" or "Operational default"
  rationale: string;    // short plain-English explanation of how the value is derived
  inTier1Text: boolean; // whether the literal value appears in src/data/tier1PolicyContent.tsx
}

const SourceBadge = ({ source, inTier1Text }: { source: Source; inTier1Text: boolean }) => {
  if (source === "tier1" && inTier1Text) return <Badge className="bg-emerald-600 hover:bg-emerald-600">Tier 1 (literal)</Badge>;
  if (source === "tier1") return <Badge variant="secondary">Tier 1 (implied)</Badge>;
  if (source === "regulatory") return <Badge className="bg-blue-600 hover:bg-blue-600">Regulatory</Badge>;
  return <Badge variant="outline" className="border-amber-500 text-amber-700">Operational default</Badge>;
};

// Static provenance metadata, keyed by table + scope
const RISK_PROV: Record<string, Provenance> = {
  var95_ceiling_pct: {
    source: "tier1",
    ref: "Tier 1 §11.2",
    rationale: "Derived from the historical 95th-percentile 1-day loss of the CBAM Composite Benchmark per profile, multiplied by a tolerance buffer (1.10× P1–P3, 1.15× P4–P5). The buffer is operational; §11.2 mandates a VaR ceiling per profile but does not state the numeric value.",
    inTier1Text: false,
  },
  tracking_error_ceiling_pct: {
    source: "tier1",
    ref: "Tier 1 §11.3",
    rationale: "Annualised tracking error ceiling vs the CBAM Composite Benchmark. §11.3 requires the ceiling exist; values calibrated from rolling 36-month realised TE plus headroom.",
    inTier1Text: false,
  },
  max_drawdown_ceiling_pct: {
    source: "tier1",
    ref: "Tier 1 §11.4",
    rationale: "Peak-to-trough drawdown ceiling. §11.4 references drawdown monitoring; numeric values set by IC on a 10-year stress lookback.",
    inTier1Text: false,
  },
  liquidity_t7_min_pct: {
    source: "tier1",
    ref: "Tier 1 §11.5 / §10.2",
    rationale: "Minimum % of portfolio realisable in 7 business days at no material discount. Aligns with §10 liquidity policy; threshold operationally set above the FCA AIFMD liquidity expectations.",
    inTier1Text: false,
  },
};

const RANGE_PROV: Provenance = {
  source: "tier1",
  ref: "Tier 1 §6.1–§6.4",
  rationale: "Strategic Asset Allocation min/max bands per risk profile. The bands themselves are stated in §6 of the Tier 1 document; this table is the authoritative editable source used for live compliance scanning.",
  inTier1Text: true,
};

const INSTR_PROV: Provenance = {
  source: "tier1",
  ref: "Tier 1 §7",
  rationale: "Permitted instrument types and conditions of use. §7 enumerates the list; this table is the operational mirror used by pre-trade checks.",
  inTier1Text: true,
};

const TAA_PROV: Record<string, Provenance> = {
  single_fund_max: {
    source: "operational",
    ref: "Operational default (IC-approved)",
    rationale: "15% single-fund cap to enforce diversification. NOT written in the Tier 1 document. Set by the Investment Committee; if codification is desired, add to §8.",
    inTier1Text: false,
  },
  sector_taa_band: {
    source: "tier1",
    ref: "Tier 1 §8.2",
    rationale: "Maximum tactical deviation from the CBAM sector benchmark weight. §8.2 references tactical bands; numeric cap is operational.",
    inTier1Text: false,
  },
  region_taa_band: {
    source: "tier1",
    ref: "Tier 1 §8.2",
    rationale: "Maximum tactical regional over/underweight relative to benchmark. §8.2 references tactical bands; numeric cap is operational.",
    inTier1Text: false,
  },
};

const SECTOR_RET_PROV: Provenance = {
  source: "operational",
  ref: "Operational (Brinson inputs)",
  rationale: "Per-sector index returns used as inputs to the Performance Attribution (Brinson) decomposition. Not a policy threshold; values are sourced from FE Analytics / FT and maintained by the Investment team.",
  inTier1Text: false,
};

const STRESS_PROV: Provenance = {
  source: "operational",
  ref: "Operational scenario library (IC-approved)",
  rationale: "Stress scenarios (e.g. GFC 2008, COVID 2020, Inflation Shock) used for §13 stress testing. §13 mandates regular stress testing; the specific scenario list, shocks and frequencies are set by the Investment Committee and not enumerated in the Tier 1 text.",
  inTier1Text: false,
};

const ESG_PROV: Provenance = {
  source: "tier1",
  ref: "Tier 1 §14 (ESG integration)",
  rationale: "Per-holding ESG scores used to flag aggregate ESG drift. §14 requires ESG integration; data is supplied by Sustainalytics/MSCI and maintained operationally.",
  inTier1Text: true,
};

const CPARTY_PROV: Provenance = {
  source: "tier1",
  ref: "Tier 1 §15 / COBS 18",
  rationale: "Per-counterparty exposure used to enforce concentration limits. §15 covers conflicts and exposure; numeric concentration caps are operational defaults set by the Risk Committee.",
  inTier1Text: false,
};

const FX_PROV: Provenance = {
  source: "operational",
  ref: "Operational (market data)",
  rationale: "Daily FX rates used to convert non-GBP holdings for compliance valuations. Not a policy threshold.",
  inTier1Text: false,
};

const CORPACT_PROV: Provenance = {
  source: "operational",
  ref: "Operational (corporate actions feed)",
  rationale: "Dividends, splits, mergers ingested for accurate position keeping. Not a policy threshold.",
  inTier1Text: false,
};

const RISK_LABELS: Record<string, string> = {
  var95_ceiling_pct: "VaR 95%",
  tracking_error_ceiling_pct: "Tracking Error",
  max_drawdown_ceiling_pct: "Max Drawdown",
  liquidity_t7_min_pct: "Liquidity T+7 min",
};

export default function ThresholdProvenance() {
  const [loading, setLoading] = useState(true);
  const [risk, setRisk] = useState<any[]>([]);
  const [ranges, setRanges] = useState<any[]>([]);
  const [instr, setInstr] = useState<any[]>([]);
  const [taa, setTaa] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      const [r1, r2, r3, r4] = await Promise.all([
        supabase.from("policy_risk_ceilings").select("*").order("profile_level"),
        supabase.from("policy_asset_class_ranges").select("*").order("profile_level").order("asset_class"),
        supabase.from("policy_permitted_instruments").select("*").order("instrument_type"),
        supabase.from("policy_taa_caps").select("*").order("scope"),
      ]);
      setRisk(r1.data || []);
      setRanges(r2.data || []);
      setInstr(r3.data || []);
      setTaa(r4.data || []);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">Threshold Provenance</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Every editable threshold on the{" "}
          <Link to="/partner/compliance/thresholds" className="text-primary underline">Policy Thresholds</Link>{" "}
          page, with its source: literal Tier 1 clause, implied by Tier 1, regulatory reference, or operational default set by the Investment / Risk Committee.
        </p>
        <div className="flex flex-wrap gap-2 mt-3 text-xs">
          <Badge className="bg-emerald-600 hover:bg-emerald-600">Tier 1 (literal)</Badge>
          <span className="text-muted-foreground">value is written in the Tier 1 policy text</span>
          <Badge variant="secondary">Tier 1 (implied)</Badge>
          <span className="text-muted-foreground">Tier 1 requires the threshold but does not state the number</span>
          <Badge className="bg-blue-600 hover:bg-blue-600">Regulatory</Badge>
          <span className="text-muted-foreground">FCA / COBS / AIFMD requirement</span>
          <Badge variant="outline" className="border-amber-500 text-amber-700">Operational default</Badge>
          <span className="text-muted-foreground">set by IC/Risk, not in Tier 1</span>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>§11 Risk Ceilings</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="text-left p-2">Threshold</th>
                <th className="text-left p-2">Per-profile values</th>
                <th className="text-left p-2">Source</th>
                <th className="text-left p-2">Reference</th>
                <th className="text-left p-2">Rationale</th>
              </tr>
            </thead>
            <tbody>
              {(Object.keys(RISK_PROV) as (keyof typeof RISK_PROV)[]).map((k) => {
                const p = RISK_PROV[k];
                const vals = risk.map((r) => `${PROFILE_NAMES[r.profile_level]}: ${r[k] ?? "—"}`).join(", ");
                return (
                  <tr key={k} className="border-b align-top">
                    <td className="p-2 font-medium">{RISK_LABELS[k]} (%)</td>
                    <td className="p-2 text-xs">{vals}</td>
                    <td className="p-2"><SourceBadge source={p.source} inTier1Text={p.inTier1Text} /></td>
                    <td className="p-2 text-xs">{p.ref}</td>
                    <td className="p-2 text-xs text-muted-foreground">{p.rationale}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>§6 Asset Class Ranges</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 mb-3">
            <SourceBadge source={RANGE_PROV.source} inTier1Text={RANGE_PROV.inTier1Text} />
            <div className="text-xs text-muted-foreground">
              <strong>{RANGE_PROV.ref}.</strong> {RANGE_PROV.rationale}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="text-left p-2">Profile</th>
                <th className="text-left p-2">Asset Class</th>
                <th className="text-right p-2">Min %</th>
                <th className="text-right p-2">Max %</th>
              </tr>
            </thead>
            <tbody>
              {ranges.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="p-2">{PROFILE_NAMES[r.profile_level]}</td>
                  <td className="p-2">{r.asset_class}</td>
                  <td className="p-2 text-right">{r.min_pct}</td>
                  <td className="p-2 text-right">{r.max_pct}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>§7 Permitted Instruments</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 mb-3">
            <SourceBadge source={INSTR_PROV.source} inTier1Text={INSTR_PROV.inTier1Text} />
            <div className="text-xs text-muted-foreground">
              <strong>{INSTR_PROV.ref}.</strong> {INSTR_PROV.rationale}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="text-left p-2">Instrument</th>
                <th className="text-left p-2">Permitted</th>
                <th className="text-left p-2">Conditions</th>
              </tr>
            </thead>
            <tbody>
              {instr.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="p-2">{r.instrument_type}</td>
                  <td className="p-2">{r.permitted ? "Yes" : "No"}</td>
                  <td className="p-2 text-muted-foreground text-xs">{r.conditions || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>§8 TAA Caps</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="text-left p-2">Scope</th>
                <th className="text-right p-2">Cap %</th>
                <th className="text-left p-2">Source</th>
                <th className="text-left p-2">Reference</th>
                <th className="text-left p-2">Rationale</th>
              </tr>
            </thead>
            <tbody>
              {taa.map((r) => {
                const p = TAA_PROV[r.scope] || {
                  source: "operational" as Source,
                  ref: "Operational default",
                  rationale: "No documented provenance — review with Compliance.",
                  inTier1Text: false,
                };
                return (
                  <tr key={r.id} className="border-b align-top">
                    <td className="p-2 font-mono text-xs">{r.scope}</td>
                    <td className="p-2 text-right">{r.cap_pct}</td>
                    <td className="p-2"><SourceBadge source={p.source} inTier1Text={p.inTier1Text} /></td>
                    <td className="p-2 text-xs">{p.ref}</td>
                    <td className="p-2 text-xs text-muted-foreground">{p.rationale}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Other configured data</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="text-left p-2">Dataset</th>
                <th className="text-left p-2">Source</th>
                <th className="text-left p-2">Reference</th>
                <th className="text-left p-2">Rationale</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["§12 Sector Index Returns", SECTOR_RET_PROV],
                ["§13 Stress Scenarios & Results", STRESS_PROV],
                ["§14 ESG Scores", ESG_PROV],
                ["§15 Counterparty Exposures", CPARTY_PROV],
                ["FX Rates", FX_PROV],
                ["Corporate Actions", CORPACT_PROV],
              ].map(([label, p]: any) => (
                <tr key={label} className="border-b align-top">
                  <td className="p-2 font-medium">{label}</td>
                  <td className="p-2"><SourceBadge source={p.source} inTier1Text={p.inTier1Text} /></td>
                  <td className="p-2 text-xs">{p.ref}</td>
                  <td className="p-2 text-xs text-muted-foreground">{p.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Notes & next steps</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2 text-muted-foreground">
          <p>
            Thresholds marked <em>Operational default</em> are not written verbatim in the Tier 1 document. If the firm wishes the policy text and configured values to mirror each other exactly, the following clauses should be added to{" "}
            <Link to="/partner/tier1-policy" className="text-primary underline inline-flex items-center gap-1">Tier 1 Policy<ExternalLink className="h-3 w-3" /></Link>:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>§8</strong> — explicit 15% single-fund cap and the sector/region TAA band figures.</li>
            <li><strong>§11</strong> — the numeric VaR, TE, drawdown and liquidity ceilings per profile.</li>
            <li><strong>§13</strong> — the named stress scenario library and required frequency.</li>
          </ul>
          <p>
            All other thresholds are either literal Tier 1 values or are operational data feeds (FX, corporate actions) not subject to policy codification.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
